import React, { useEffect, useState } from 'react';
import './style.scss';
import {
  AgreeDocumentMutationVariables,
  Exact,
  GetAgreementDocumentQuery,
  useAgreeDocumentMutation,
} from 'views/utils/graphql/graphql';
import { InputConsent } from 'views/mutual/inputConsent';
import { Button, ButtonFill, ButtonSize, ButtonType } from 'views/mutual/button';
import { UseQueryState } from 'urql';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { useGlobalToastHandler } from 'views/mutual/globalToast/hooks';
import { ConsentLink } from 'views/mutual/inputConsent/link';

enum AgreementDocumentType {
  termsAndConditions = 1,
}

type AgreementDocumentProps = {
  agreementDocument: UseQueryState<
    GetAgreementDocumentQuery,
    Exact<{
      [key: string]: never;
    }>
  >;
  onRequestClose?: () => void;
};

export const AgreementDocument = (props: AgreementDocumentProps) => {
  const { onRequestClose, agreementDocument } = props;

  const [agreeData, agree] = useAgreeDocumentMutation();
  const { showError } = useGlobalToastHandler();

  const initialCheckList = Object.entries(AgreementDocumentType).map(([_, v]) => {
    return {
      type: v as AgreementDocumentType,
      checked: false,
    };
  });
  const [checkList, setCheckList] = useState(initialCheckList);
  const [agreeTypes, setAgreeTypes] = useState<number[]>([]);

  const agreeSubmit = useOnceSubmit<AgreeDocumentMutationVariables>(
    {
      onSubmit: async (variables) => {
        const res = await agree(variables);
        if (res.data?.AgreeDocument?.Code === 0) {
          onRequestClose && onRequestClose();
        } else if (res.data) {
          showError(res.data.AgreeDocument?.Message);
        }
      },
    },
    [],
  );

  useEffect(() => {
    if (agreeTypes.length === 0) return;
    const variables: AgreeDocumentMutationVariables = {
      input: {
        Types: agreeTypes,
      },
    };
    agreeSubmit(variables);
  }, [agreeTypes]);

  if (agreementDocument.fetching) return;
  if (!agreementDocument.data?.GetAgreementDocument?.Data) return;

  const aggreementDocumentInfoList = [
    {
      type: AgreementDocumentType.termsAndConditions,
      title: 'FPGオンライン取引・電子署名取引一般規約についての同意',
      checkText: '変更後の「FPGオンライン取引・電子署名取引一般規約」が適用されることに同意します',
      linkTitle: 'FPGオンライン取引・電子署名取引一般規約',
      comparisonTable: '新旧対照表',
      changeDetails: 'この変更について',
    },
  ];

  // 未読のものだけを抽出して表示用に加工する
  const targetData = agreementDocument.data.GetAgreementDocument.Data.filter((d) => !d?.Read).map((d) =>
    Object.assign(
      {},
      d,
      aggreementDocumentInfoList.find((dt) => dt.type === d?.Type),
    ),
  );

  const handleAgreeCheck = () => {
    setAgreeTypes(checkList.filter((d) => d.checked).map((d) => d.type));
  };

  const disableAgreeButton = () => {
    return checkList.some((d) => targetData.find((x) => x.type === d.type) && !d.checked);
  };

  return (
    <div className="agreementWrapper">
      <div className="agreementWrapper_title">規約変更の同意</div>
      {targetData.map((d) =>
        d ? (
          <InputConsent
            title={d.title}
            name={AgreementDocumentType[d.Type]}
            checkText={d.checkText}
            leftLinkTitle={d.linkTitle}
            leftLinkUrl={d?.TermsAndConditionsURL}
            rightLinkTitle={d.comparisonTable}
            rightLinkUrl={d?.ComparisonTableURL}
            batchDangerWord="必須"
            isChecked={checkList.find((cl) => cl.type === d.type)?.checked ?? false}
            setIsChecked={(e) => {
              setCheckList((prev) =>
                prev.map((x) => (x.type === d.type ? { checked: e!.target.checked, type: d.type } : x)),
              );
            }}
            isEnable={false}
          />
        ) : null,
      )}
      <Button
        label="同意する"
        type={ButtonType.Button}
        buttonFill={disableAgreeButton() ? ButtonFill.white : ButtonFill.primary}
        buttonSize={ButtonSize.medium}
        onClick={() => {
          if (disableAgreeButton()) return;
          handleAgreeCheck();
        }}
      />
      {targetData.map((d) => (d ? <ConsentLink linkTitle={d.changeDetails} linkUrl={d?.ChangeDetailsURL} /> : null))}
    </div>
  );
};
